import React, { useId } from 'react';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { borderRadius, getSemanticSurfaceTokens } from '../../../theme/theme';
import { OperationProgressIndicator, usePrefersReducedMotion } from '../progress';
import { BodyText, CompactPageTitle } from '../typography';

export type UnsavedChangesChoice = 'save' | 'discard' | 'cancel';

export interface UnsavedChangesDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  saveLabel?: string;
  discardLabel?: string;
  cancelLabel?: string;
  /** Names the operation the chosen action started; `null` while idle. */
  busyPhase?: string | null;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}

/**
 * Shared app-styled "unsaved changes" dialog with three choices: save the
 * pending edits and continue, continue without saving (discarding them from
 * the outgoing operation), or cancel and stay. Visual language matches
 * ConfirmationDialog.
 */
export const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
  open,
  title,
  message,
  saveLabel = 'Save',
  discardLabel = "Don't save",
  cancelLabel = 'Cancel',
  busyPhase = null,
  onSave,
  onDiscard,
  onCancel,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const fallbackTitleId = useId();
  const descriptionId = useId();
  const reducedMotion = usePrefersReducedMotion();
  const busy = Boolean(busyPhase);

  return (
    <Dialog
      open={open}
      // While the chosen action runs there is no answer left to give, so
      // Escape and backdrop dismissal must not resolve it behind its back.
      onClose={busy ? undefined : onCancel}
      transitionDuration={reducedMotion ? 0 : undefined}
      aria-labelledby={fallbackTitleId}
      aria-describedby={descriptionId}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${borderRadius.lg}px`,
          border: `1px solid ${colors.border.strong}`,
          bgcolor: theme.palette.background.paper,
          backgroundImage: 'none',
          boxShadow: `0 18px 42px ${alpha(theme.palette.common.black, 0.22)}`,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id={fallbackTitleId}
        sx={{
          px: 2,
          pt: 1.65,
          pb: 0.9,
          borderBottom: `1px solid ${colors.border.light}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <WarningAmberIcon color="warning" fontSize="small" />
        <CompactPageTitle component="span" sx={{ color: colors.text.primary }}>
          {title}
        </CompactPageTitle>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pt: 1.75, pb: 0.75 }}>
        <BodyText id={descriptionId} secondary sx={{ color: colors.text.secondary }}>
          {message}
        </BodyText>
        {busy && (
          <Box
            role="status"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}
          >
            {/* The words carry the phase; the ring only repeats it visually. */}
            <OperationProgressIndicator presentational size={14} />
            <BodyText sx={{ color: colors.text.primary }}>{busyPhase}</BodyText>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pt: 0.5, pb: 1.5, gap: 0.5 }}>
        <Button
          size="small"
          onClick={onCancel}
          color="primary"
          disabled={busy}
          // Staying put is the only choice that neither writes nor drops work,
          // so it is where an accidental Enter must land.
          autoFocus
          sx={{ minHeight: 30, borderRadius: `${borderRadius.sm}px` }}
        >
          {cancelLabel}
        </Button>
        <Button
          size="small"
          onClick={onDiscard}
          color="error"
          disabled={busy}
          sx={{ minHeight: 30, borderRadius: `${borderRadius.sm}px` }}
        >
          {discardLabel}
        </Button>
        <Button
          size="small"
          onClick={onSave}
          color="primary"
          variant="contained"
          disabled={busy}
          sx={{ minHeight: 30, borderRadius: `${borderRadius.sm}px` }}
        >
          {saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
